import type { Rng } from '../../core/rng';
import { clamp } from '../../core/math';
import type { PartyWalls, Stretch } from './partyWalls';
import type { ShopFronts } from './shops';

export type OpeningKind = 'window' | 'door' | 'loftDoor' | 'smallWindow' | 'shop';

/**
 * A rectangular opening in wall coordinates: x from the left end of the wall as seen from
 * outside, y up from the ground.
 */
export interface Opening {
  readonly kind: OpeningKind;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Doors: height of the leaf; the transom light fills the rest. */
  readonly leaf?: number;
}

/** What the layout needs to know about a house; widths and heights in metres. */
export interface FacadeSpec {
  /** Front and back wall length. */
  readonly width: number;
  /** Side wall length, front wall to back wall. */
  readonly length: number;
  readonly storeys: number;
  readonly storeyHeight: number;
  readonly pitch: number;
  readonly party: PartyWalls;
  /** Where the ground floor sells (shops.ts). */
  readonly shop: ShopFronts;
}

/** Gable openings sit above the eaves, in the coordinates of the wall below them. */
export interface HouseOpenings {
  readonly front: readonly Opening[];
  readonly back: readonly Opening[];
  readonly left: readonly Opening[];
  readonly right: readonly Opening[];
  readonly frontGable: readonly Opening[];
  readonly backGable: readonly Opening[];
}

/** STYLE §8.1 opening rules. */
export const OPENING = {
  bayModule: 3.3,
  maxBays: 4,
  windowToBay: 0.466,
  windowWidth: { min: 1.0, max: 1.55 },
  heightToWidth: 1.98,
  /**
   * Least height over width of a cross window, as on Begijnhof 34 (c. 1528) and the painted
   * window row; a storey too low for a window of the bay's width gets a narrower one.
   */
  minHeightToWidth: 1.8,
  headroom: 1.0,
  sill: 0.9,
  mullion: 0.12,
  transomAt: 0.55,
  door: { width: 1.0, leaf: 2.2, transom: 0.5 },
  /**
   * A window less than this much wider than the door takes the door's width, so its jambs line
   * up over the door's instead of standing a needle of wall away from them.
   */
  jambAlign: 0.02,
  smallWindow: { width: 0.6, gableHeight: 0.9, sideHeight: 1.0 },
  loftDoor: { width: 1.0, height: 1.6, sill: 0.3, rate: 0.6 },
  /** Perpendicular gap from a gable opening to the gable outline; the coping takes 0.11 of it. */
  gableClearance: 0.3,
  /** Least wall left beside a side window. */
  sideMargin: 0.3,
  /**
   * Least length of a stretch of a side wall that a neighbour covers in part and leaves open
   * elsewhere, for a side window; centred on it, the window keeps 0.7 m from both its ends.
   */
  openStretch: 2.0,
  /**
   * A shop sells over its lowered lower shutter and under its raised upper one (Rouwhorst, Binnenstad
   * 214 and 222): an opening from the windows' sill to the door's head, the two shutters each half of
   * it. Widths are estimates (docs/plans/houses.md, "Shops"). A one-bay front keeps its door `margin`
   * from one corner and the shop as far from the door and the other corner; a corner shop opens
   * `side.width` of the side street's wall, `side.fromFront` back from the front corner.
   */
  shop: { widthToBay: 0.75, width: { min: 1.2, max: 2.6 }, margin: 0.4, side: { width: 2.0, fromFront: 1.0 } },
} as const;

/* A stretch's ends are differences of lot corners, so one laid out exactly `openStretch` long comes out a hair short. */
const STRETCH_NOISE = 1e-9;

interface Size {
  readonly width: number;
  readonly height: number;
}

export function bayCount(width: number): number {
  return clamp(Math.round(width / OPENING.bayModule), 1, OPENING.maxBays);
}

export function windowSize(bay: number, storeyHeight: number): Size {
  const { min, max } = OPENING.windowWidth;
  const bayWidth = clamp(OPENING.windowToBay * bay, min, max);
  const height = Math.min(OPENING.heightToWidth * bayWidth, storeyHeight - OPENING.headroom);
  const width = clamp(height / OPENING.minHeightToWidth, min, bayWidth);
  return { width: width - OPENING.door.width < OPENING.jambAlign ? OPENING.door.width : width, height };
}

/** Highest point any opening of a storey reaches, as a window of the tallest allowed size. */
function storeyTop(storeyHeight: number): number {
  return OPENING.sill + storeyHeight - OPENING.headroom;
}

function centred(kind: OpeningKind, centre: number, y: number, size: Size): Opening {
  return { kind, x: centre - size.width / 2, y, width: size.width, height: size.height };
}

/** A door standing on the ground, centred at `centre`, its head no higher than a window's. */
export function doorAt(centre: number, storeyHeight: number): Opening {
  const { width, leaf, transom } = OPENING.door;
  const height = Math.min(leaf + transom, storeyTop(storeyHeight));
  return { ...centred('door', centre, 0, { width, height }), leaf: Math.min(leaf, height) };
}

/** A shop opening from the windows' sill to the head of the storey's door. */
function shopAt(centre: number, width: number, storeyHeight: number): Opening {
  const head = doorAt(0, storeyHeight).height;
  return centred('shop', centre, OPENING.sill, { width, height: head - OPENING.sill });
}

/** A shop's ground floor: the door in its bay and a shop in every other, or, on one bay, the two side by side. */
function shopGroundFloor(spec: FacadeSpec, bays: number, doorBay: number, doorLeft: boolean): Opening[] {
  const { widthToBay, width, margin } = OPENING.shop;
  const bay = spec.width / bays;
  if (bays > 1) {
    const shopWidth = clamp(widthToBay * bay, width.min, width.max);
    return Array.from({ length: bays }, (_, b) =>
      b === doorBay ? doorAt((b + 0.5) * bay, spec.storeyHeight) : shopAt((b + 0.5) * bay, shopWidth, spec.storeyHeight)
    );
  }
  const door = OPENING.door.width;
  const room = spec.width - 3 * margin - door;
  if (room < width.min) return [doorAt(spec.width / 2, spec.storeyHeight)];
  const [doorCentre, shopCentre] = doorLeft
    ? [margin + door / 2, (2 * margin + door + spec.width - margin) / 2]
    : [spec.width - margin - door / 2, (margin + spec.width - 2 * margin - door) / 2];
  return [doorAt(doorCentre, spec.storeyHeight), shopAt(shopCentre, Math.min(room, width.max), spec.storeyHeight)];
}

/** One opening per bay and storey; the ground floor has the door in `doorBay`, and a shop's ground floor its shop openings. */
function frontOpenings(spec: FacadeSpec, bays: number, size: Size, doorBay: number, doorLeft: boolean): Opening[] {
  const bay = spec.width / bays;
  const openings: Opening[] = spec.shop.front ? shopGroundFloor(spec, bays, doorBay, doorLeft) : [];
  for (let storey = spec.shop.front ? 1 : 0; storey < spec.storeys; storey++) {
    for (let b = 0; b < bays; b++) {
      openings.push(
        storey === 0 && b === doorBay
          ? doorAt((b + 0.5) * bay, spec.storeyHeight)
          : centred('window', (b + 0.5) * bay, storey * spec.storeyHeight + OPENING.sill, size)
      );
    }
  }
  return openings;
}

function backOpenings(spec: FacadeSpec, bays: number, size: Size): Opening[] {
  const count = Math.max(1, bays - 1);
  const bay = spec.width / count;
  const openings: Opening[] = [];
  for (let storey = 0; storey < spec.storeys; storey++) {
    for (let b = 0; b < count; b++) {
      openings.push(centred('window', (b + 0.5) * bay, storey * spec.storeyHeight + OPENING.sill, size));
    }
  }
  return openings;
}

/** The stretches of a wall `length` long that none of `covered` reaches, from its back end. */
export function openStretches(length: number, covered: readonly Stretch[]): Stretch[] {
  const open: Stretch[] = [];
  let reached = 0;
  for (const [from, to] of [...covered].sort((a, b) => a[0] - b[0])) {
    if (from > reached) open.push([reached, Math.min(from, length)]);
    reached = Math.max(reached, to);
  }
  if (reached < length) open.push([reached, length]);
  return open.filter(([from, to]) => to > from);
}

/** One small window per storey in the middle of each stretch of a side wall no neighbour covers. */
function sideWindows(spec: FacadeSpec, side: 'left' | 'right'): Opening[] {
  const { width, sideHeight } = OPENING.smallWindow;
  const covered = spec.party.covered[side];
  const least = covered.length === 0 ? width + 2 * OPENING.sideMargin : OPENING.openStretch;
  const middles = openStretches(spec.length, covered)
    .filter(([from, to]) => to - from >= least - STRETCH_NOISE)
    .map(([from, to]) => (from + to) / 2);
  /* Seen from outside, the left wall starts at the back corner and the right wall at the front. */
  const openings: Opening[] = [];
  for (const middle of middles) {
    const x = side === 'left' ? middle : spec.length - middle;
    for (let storey = 0; storey < spec.storeys; storey++) {
      openings.push(
        centred('smallWindow', x, storey * spec.storeyHeight + OPENING.sill, { width, height: sideHeight })
      );
    }
  }
  return openings;
}

/** A corner shop's opening on a side wall, near the front corner, where no neighbour covers the wall. */
function sideShop(spec: FacadeSpec, side: 'left' | 'right'): Opening | null {
  const { width, fromFront } = OPENING.shop.side;
  const [start, end] = [spec.length - fromFront - width, spec.length - fromFront];
  const margin = OPENING.sideMargin;
  const open = openStretches(spec.length, spec.party.covered[side]).some(([from, to]) => from <= start - margin && to >= end + margin);
  if (!open || start - margin < 0) return null;
  const centre = side === 'left' ? (start + end) / 2 : spec.length - (start + end) / 2;
  return shopAt(centre, width, spec.storeyHeight);
}

/* A ground-floor side window nearer the shop than `margin` would leave a needle of wall between them. */
function clearOf(shop: Opening, margin: number): (o: Opening) => boolean {
  return (o) => o.y >= shop.y + shop.height || o.x + o.width <= shop.x - margin || o.x >= shop.x + shop.width + margin;
}

function sideOpenings(spec: FacadeSpec, side: 'left' | 'right'): Opening[] {
  const windows = sideWindows(spec, side);
  const shop = spec.shop[side] ? sideShop(spec, side) : null;
  return shop ? [...windows.filter(clearOf(shop, OPENING.sideMargin)), shop] : windows;
}

/** What the openings of a gable depend on: the wall under it and the roof pitch. */
export type GableSpec = Pick<FacadeSpec, 'width' | 'storeys' | 'storeyHeight' | 'pitch'>;

/** Least perpendicular distance from the top corners of a gable opening to the gable slopes. */
export function gableClearance(spec: GableSpec, opening: Opening): number {
  const half = spec.width / 2;
  const rise = half * Math.tan(spec.pitch);
  const above = opening.y + opening.height - spec.storeys * spec.storeyHeight;
  const inset = Math.min(opening.x, spec.width - opening.x - opening.width);
  return (inset * rise - above * half) / Math.hypot(rise, half);
}

function gableOpening(spec: GableSpec, kind: OpeningKind, floor: number): Opening {
  const centre = spec.width / 2;
  if (kind === 'loftDoor') {
    const { width, height, sill } = OPENING.loftDoor;
    return centred(kind, centre, floor + sill, { width, height });
  }
  const { width, gableHeight } = OPENING.smallWindow;
  return centred('smallWindow', centre, floor + OPENING.sill, { width, height: gableHeight });
}

/**
 * One opening per attic storey, centred under the apex: `first` on the lowest when it fits,
 * else a small window, then small windows upward until the gable grows too narrow.
 */
export function gableOpenings(spec: GableSpec, first: OpeningKind): Opening[] {
  const eaves = spec.storeys * spec.storeyHeight;
  const fits = (o: Opening) => gableClearance(spec, o) >= OPENING.gableClearance;
  const lowest = [gableOpening(spec, first, eaves), gableOpening(spec, 'smallWindow', eaves)].find(fits);
  if (!lowest) return [];
  const above: Opening[] = [];
  for (let level = 1; ; level++) {
    const next = gableOpening(spec, 'smallWindow', eaves + level * spec.storeyHeight);
    if (!fits(next)) break;
    above.push(next);
  }
  return [lowest, ...above];
}

/**
 * A front's bays, their window size and the bay with the door; a one-bay shop's door goes to the side
 * drawn from its own fork, so no other house's draws move.
 */
function frontLayout(spec: FacadeSpec, rng: Rng): { bays: number; size: Size; doorBay: number; doorLeft: boolean } {
  const bays = bayCount(spec.width);
  const size = windowSize(spec.width / bays, spec.storeyHeight);
  const doorBay = bays === 1 || rng.fork('door').chance(0.5) ? 0 : bays - 1;
  const doorLeft = bays === 1 && spec.shop.front ? rng.fork('shopDoor').chance(0.5) : doorBay === 0;
  return { bays, size, doorBay, doorLeft };
}

export function openingsFor(spec: FacadeSpec, rng: Rng): HouseOpenings {
  const { bays, size, doorBay, doorLeft } = frontLayout(spec, rng);
  const loft = rng.fork('loft').chance(OPENING.loftDoor.rate);
  return {
    front: frontOpenings(spec, bays, size, doorBay, doorLeft),
    back: backOpenings(spec, bays, size),
    left: sideOpenings(spec, 'left'),
    right: sideOpenings(spec, 'right'),
    frontGable: gableOpenings(spec, loft ? 'loftDoor' : 'smallWindow'),
    backGable: gableOpenings(spec, 'smallWindow'),
  };
}

/**
 * A back building stands with its back and sides on the plot's lines, against the buildings of the
 * plots around it, so it opens only onto its own courtyard or gang, and its gables stay blank (PLAN
 * Lots, back-building simplifications): its front as a house's front, nothing else.
 */
export function courtyardOpenings(spec: FacadeSpec, rng: Rng): HouseOpenings {
  const { bays, size, doorBay, doorLeft } = frontLayout(spec, rng);
  return {
    front: frontOpenings(spec, bays, size, doorBay, doorLeft),
    back: [],
    left: [],
    right: [],
    frontGable: [],
    backGable: [],
  };
}
